import { PURGE } from "redux-persist";
import { createSlice } from "@reduxjs/toolkit";

import type { AuthState } from "@/types/store";
import {
  changePassword,
  getMeInfo,
  loginUser,
  onboardUser,
  setPassword,
  verifyUser,
} from "@/api/auth/thunks";
import {
  answerQuestionnaire,
  getQuestionnaireAnswered,
} from "@/api/profile/thunks";
import { getQuestionnaire } from "@/api/profile/thunks";
import { USER_STATUS } from "@/utils/constants";

const initialState: AuthState = {
  user: null,
  userEmail: "",
  isAuthorized: false,
  rememberMe: false,
  permissions: [],
  token: "",
  userType: null,
  loading: false,
  error: null,
  mfaToken: "",
  rememberedUsers: {},
  questionnaireData: {
    id: null,
    questions: [],
    answers: {},
  },
};

export const AuthSlice = createSlice({
  name: "AuthSlice",
  initialState,
  reducers: {
    setUser: (state, { payload }) => {
      state.user = payload;
    },
    setUserType: (state, { payload }) => {
      state.userType = payload;
    },
    setUserEmail: (state, { payload }) => {
      state.userEmail = payload;
    },
    setStatus: (state, { payload }) => {
      if (!state.user) return;

      state.user.status = payload;
    },
    setRememberMe: (state, { payload }) => {
      state.rememberMe = payload.rememberMe;
      if (payload.rememberMe && payload.email) {
        if (!state.rememberedUsers[payload.email]) {
          state.rememberedUsers[payload.email] = new Date().getTime();
        }
      }
      if (!payload.rememberMe && payload.email) {
        delete state.rememberedUsers[payload.email];
      }
    },
    userLoggedOut: (state) => {
      const { mfaToken, rememberedUsers, rememberMe } = state;
      return {
        ...initialState,
        mfaToken: rememberMe ? mfaToken : "",
        rememberedUsers,
      };
    },
    updateUser: (state, { payload }) => {
      state.user = { ...state.user, ...payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => {
        return initialState;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.userEmail = payload?.email;
        state.isAuthorized = false;
        if (payload?.accessToken) {
          state.user = payload.user;
          if (state.user) state.user.status = payload.status;
          state.userType = payload.role;
          state.token = payload.accessToken;
          state.mfaToken = state.mfaToken || payload.mfaToken;
          state.permissions = payload.permissions;
          state.isAuthorized = true;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(setPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(setPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isAuthorized = false;
      })
      .addCase(verifyUser.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.user = payload.user;
        if (state.user) state.user.status = payload.status;
        state.userType = payload.role;
        state.token = payload.accessToken;
        state.mfaToken = payload.mfaToken;
        state.permissions = payload.permissions;
        state.isAuthorized = true;
      })
      .addCase(verifyUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthorized = false;
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.token = payload.accessToken;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getQuestionnaire.fulfilled, (state, { payload }) => {
        state.questionnaireData = {
          id: payload.id,
          questions: payload.questions,
          answers: state.questionnaireData.answers,
        };
      })
      .addCase(getQuestionnaireAnswered.fulfilled, (state, { payload }) => {
        if (payload.answered) {
          const answersObject = payload.answered.reduce(
            (
              acc: { [key: string]: string },
              answer: { [key: string]: string }
            ) => {
              const question = Object.keys(answer)[0];
              acc[question] = answer[question];
              return acc;
            },
            {}
          );
          state.questionnaireData.answers = answersObject;
        }
      })
      .addCase(answerQuestionnaire.fulfilled, (state, { payload }) => {
        const answers = payload.answered.reduce(
          (
            acc: { [key: string]: string },
            answer: { [key: string]: string }
          ) => {
            const question = Object.keys(answer)[0];
            acc[question] = answer[question];
            return acc;
          },
          {}
        );
        state.questionnaireData.answers = answers;
      })
      .addCase(onboardUser.fulfilled, (state) => {
        if (state.user) state.user.status = USER_STATUS.ACTIVE;
      })
      .addCase(getMeInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMeInfo.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.user = payload.user;
        state.userType = payload.role;
        state.permissions = payload.permissions;
        if (state.user) state.user.status = payload.status;
      })
      .addCase(getMeInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setUser,
  setUserType,
  setUserEmail,
  setStatus,
  setRememberMe,
  userLoggedOut,
  updateUser,
} = AuthSlice.actions;

export default AuthSlice.reducer;
